import React, { useEffect, useState } from 'react';

import { css } from '@emotion/css';
import { Divider, notification } from 'antd';
import * as queryString from 'query-string';

import { Button } from 'components/Button';
import { Container } from 'components/Container';
import { DetailPageSections } from 'components/recipes/detailPage/DetailPageSections';
import { Typography } from 'components/Typography';

import { truthy } from 'utils/boolean';
import { formatUSD } from 'utils/formatting';

import { PaymentDeprecationBanner } from './common/paymentConstants';
import { PaymentInfo } from './tabs/PaymentInfoModal';
import './styles/PaymentDetailsPage.css';

export const PaymentBreakdownLine = ({ label, value }: { label: string; value: string }) => (
  <div
    className={css`
      display: flex;
      justify-content: space-between;
      max-width: 550px;
      margin-bottom: 4px;
    `}
  >
    <Typography type="subtitle">{label}:</Typography>
    <Typography style={{ fontWeight: 600 }} type="subtitle">
      {value}
    </Typography>
  </div>
);

export const OrderPayPage = () => {
  // Removed activeTenant since it's not used anymore
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo>({
    orderNo: '',
    invoiceNo: '',
    customerId: '',
    customerName: '',
    contactId: '',
    contactName: '',
    companyId: '',
    companyName: '',
    shipToId: '',
    shipToName: '',
    requestedDate: '',
    rmaExpirationDate: '',
    salesSubTotal: '',
    taxTotal: '',
    cfTotalDue: '',
    totalFreight: '',
    totalOtherCharge: '',
    downpayment: '',
    downpaymentPaid: '',
  });

  useEffect(() => {
    const queryParams = queryString.parse(window.location.search);
    const decodedQueryParams = {} as PaymentInfo;
    // eslint-disable-next-line guard-for-in
    for (const key in queryParams) {
      // convert snake_case to camelCase
      const camelCaseKey = key.replace(/_\w/g, (match) => match[1].toUpperCase());
      decodedQueryParams[camelCaseKey as keyof PaymentInfo] = queryParams[key] as string;
    }

    if (!decodedQueryParams.orderNo || decodedQueryParams.orderNo.trim() === '') {
      // show a notification for missing orderNo then close the window:
      notification.error({
        message: 'Missing Order Number!',
        description: 'This window will close in a few seconds. Reopen this window should fix this issue.',
        duration: 10,
        onClose: () => {
          window.close();
        },
        onClick: () => {
          window.close();
        },
      });
    } else {
      setPaymentInfo(decodedQueryParams);
    }
  }, []);

  return (
    <Container
      className={css`
        width: 100%;
        padding: 0 48px;
        margin: 0px;
      `}
    >
      <PaymentDeprecationBanner />
      <div
        className={css`
          margin: 32px 0;
          padding: 24px;
          border: 1px solid #f5222d;
          border-radius: 4px;
          background-color: #fff1f0;
        `}
      >
        <Typography style={{ fontWeight: 'bold', marginBottom: '16px', color: '#cf1322' }}>
          Payment Processing Discontinued
        </Typography>
        <Typography
          style={{
            marginTop: '24px',
            fontStyle: 'italic',
            color: '#666',
          }}
        >
          To view existing transactions, please refer to the Payment Portal.
        </Typography>
      </div>
      <div
        className={css`
          margin-bottom: 28px;
          display: flex;
          gap: 6px;
        `}
      >
        <Typography type="large" style={{ fontWeight: 'bold' }}>
          Order #{paymentInfo.orderNo}
        </Typography>
        <Typography type="large" style={{ fontWeight: 'normal' }}>
          Details
        </Typography>
      </div>
      <DetailPageSections
        sections={[
          {
            title: 'Order Information',
            rows: [
              [
                { label: `Customer: `, value: `${paymentInfo.customerName} `, id: paymentInfo.customerId },
                paymentInfo.contactId && {
                  label: `Contact: `,
                  value: `${paymentInfo.contactName} `,
                  id: paymentInfo.contactId,
                },
                { label: `Ship To: `, value: `${paymentInfo.shipToName} `, id: paymentInfo.shipToId },
              ].filter(truthy),
            ],
          },
          {
            title: 'Order Summary',
            children: (
              <>
                <PaymentBreakdownLine label="Order Subtotal" value={formatUSD(paymentInfo.salesSubTotal, true, 2)} />
                <PaymentBreakdownLine label="Tax" value={formatUSD(paymentInfo.taxTotal, true, 2)} />
                <PaymentBreakdownLine label="Freight" value={formatUSD(paymentInfo.totalFreight, true, 2)} />
                {paymentInfo.totalOtherCharge && Number(paymentInfo.totalOtherCharge) > 0 && (
                  <PaymentBreakdownLine label="Other Charge" value={formatUSD(paymentInfo.totalOtherCharge, true, 2)} />
                )}
                <PaymentBreakdownLine label="Grand Total" value={formatUSD(Number(paymentInfo.cfTotalDue), true, 2)} />
              </>
            ),
          },
        ]}
      />
      <Divider
        className={css`
          position: fixed;
          bottom: 36px;
        `}
      />
      <Button
        onClick={() => window.close()}
        type="default"
        className={css`
          position: fixed;
          bottom: 10px;
          right: 10px;
        `}
      >
        Close
      </Button>
    </Container>
  );
};
